import React from "react";
import { ApiProcessingRecord } from "../../../../types/prodcut-data-review/product-data.types";
import { ProcessSubsections } from "./components/ProcessSubsections";
import { GateKind } from "./types/processDetail.types";
import { useProcessChemicalCrud } from "./hooks/useProcessChemicalCrud";
import { useProcessDetail } from "./hooks/useProcessDetail";
import { useProcessDetailActions } from "./hooks/useProcessDetailActions";
import { useProcessWasteCrud } from "./hooks/useProcessWasteCrud";

interface ProcessDetailPanelProps {
  process: ApiProcessingRecord;
  gateKind: GateKind;
  disabled?: boolean;
  /** Persists the process record (CIP/SIP rides the process PUT — see ProcessRecordsList.saveCipSip). */
  onSaveCipSip: (updated: ApiProcessingRecord) => void;
}

export const ProcessDetailPanel: React.FC<ProcessDetailPanelProps> = ({
  process,
  gateKind,
  disabled = false,
  onSaveCipSip
}) => {
  const { detail, chemicals, wastes, cipSipRecords, isLoading, isError, refetch, lookups } = useProcessDetail({
    processId: process._id,
    gateKind,
    enabled: true
  });

  const chemicalCrud = useProcessChemicalCrud({
    processId: process._id,
    gateKind,
    onSaved: refetch
  });

  const wasteCrud = useProcessWasteCrud({
    processId: process._id,
    gateKind,
    ingredientNameMap: lookups.ingredientNameMap,
    onSaved: refetch
  });

  const actions = useProcessDetailActions({ chemicals, wastes, detail, chemicalCrud, wasteCrud, onSaveCipSip });

  if (isLoading) {
    return (
      <div className="tw-text-xs tw-text-muted-foreground tw-py-3 tw-px-1">Loading process details…</div>
    );
  }

  if (isError || !detail) {
    return (
      <div className="tw-text-xs tw-text-destructive tw-py-3 tw-px-1">Failed to load process details.</div>
    );
  }

  return (
    <ProcessSubsections
      process={process}
      gateKind={gateKind}
      disabled={disabled}
      chemicals={chemicals}
      wastes={wastes}
      cipSipRecords={cipSipRecords}
      onAddChemical={chemicalCrud.addChemical}
      onUpdateChemical={actions.onUpdateChemical}
      onRemoveChemical={chemicalCrud.removeChemical}
      onAddRoute={actions.onAddRoute}
      onUpdateRoute={actions.onUpdateRoute}
      onRemoveRoute={actions.onRemoveRoute}
      onAddWaste={wasteCrud.addWaste}
      onUpdateWaste={actions.onUpdateWaste}
      onRemoveWaste={wasteCrud.removeWaste}
      onAddCip={actions.onAddCip}
      onUpdateCip={actions.onUpdateCip}
      onRemoveCip={actions.onRemoveCip}
    />
  );
};

export default ProcessDetailPanel;
